/**
 * Construction trades — canonical list used by:
 *  - NewBidProjectModal scope entry (Trade dropdown)
 *  - Bid Sheet aggregation (group by trade)
 *  - Contractor profile categories
 *
 * Order: structural/site → mechanicals → exterior → interior → finish → ops.
 * Each row carries a stable `key` (snake_case) and a human `label`.
 *
 * "Other" is intentionally last and acts as a free-text fallback path.
 */
export const TRADES = [
  { key: 'general',         label: 'General Contractor' },
  { key: 'excavation',      label: 'Excavation / Grading' },
  { key: 'concrete',        label: 'Concrete / Foundation' },
  { key: 'framing',         label: 'Framing / Carpentry' },
  { key: 'roofing',         label: 'Roofing' },
  { key: 'siding',          label: 'Siding / Exterior' },
  { key: 'windows_doors',   label: 'Windows & Doors' },
  { key: 'plumbing',        label: 'Plumbing' },
  { key: 'electrical',      label: 'Electrical' },
  { key: 'hvac',            label: 'HVAC' },
  { key: 'insulation',      label: 'Insulation' },
  { key: 'drywall',         label: 'Drywall' },
  { key: 'painting',        label: 'Painting' },
  { key: 'flooring',        label: 'Flooring' },
  { key: 'tile',            label: 'Tile / Stone' },
  { key: 'cabinetry',       label: 'Cabinets / Counters' },
  { key: 'trim_finish',     label: 'Trim / Finish Carpentry' },
  { key: 'appliances',      label: 'Appliances / Fixtures' },
  { key: 'landscaping',     label: 'Landscaping' },
  { key: 'cleaning',        label: 'Cleaning' },
  { key: 'permits',         label: 'Permits / Inspections' },
  { key: 'demo',            label: 'Demolition' },
  { key: 'other',           label: 'Other (specify in notes)' },
];

/** Trade key → friendly label. Falls back to title-cased key if unknown. */
export function tradeLabel(key) {
  if (!key) return '';
  const hit = TRADES.find(t => t.key === key);
  if (hit) return hit.label;
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/** Short scope hint per trade — used inside the default bid instructions. */
const TRADE_SCOPE_HINTS = {
  general: 'oversee and coordinate all trades, scheduling, and site supervision',
  excavation: 'site excavation, grading, and rough grading',
  concrete: 'foundation, footings, and flatwork concrete',
  framing: 'rough framing and structural carpentry',
  roofing: 'the roofing system, underlayment, and flashing',
  siding: 'exterior siding and cladding',
  windows_doors: 'furnish and install all windows and exterior doors',
  plumbing: 'rough and finish plumbing, fixtures, and connections',
  electrical: 'rough and finish electrical, panel, devices, and fixtures',
  hvac: 'HVAC equipment, ductwork, and controls',
  insulation: 'insulation at walls, ceilings, and applicable assemblies',
  drywall: 'drywall hang, tape, and finish',
  painting: 'interior and exterior painting and finishes',
  flooring: 'furnish and install all flooring',
  tile: 'tile and stone installation',
  cabinetry: 'cabinetry and countertop furnish and install',
  trim_finish: 'interior trim and finish carpentry',
  appliances: 'appliance and fixture furnish and install',
  landscaping: 'landscaping and site finishes',
  cleaning: 'final construction cleaning',
  permits: 'permits and required inspections',
  demo: 'demolition and debris removal',
  other: 'the work described below',
};

/**
 * Trade-specific default bid instructions. Always asks the contractor to bid
 * the attached plans for their trade. Auto-filled into a scope's notes when a
 * trade is picked; fully editable by the user afterward.
 */
export function tradeBidTemplate(key) {
  const label = tradeLabel(key) || 'the specified';
  const hint = TRADE_SCOPE_HINTS[key] || 'the work described below';
  return `Please review the attached plans and specifications and submit your bid for all ${label} work — ${hint} — exactly as shown on the drawings. Include all labor, materials, equipment, and permits required to complete this scope per plan. Note any assumptions, exclusions, or alternates.`;
}

const _TRADE_TEMPLATE_SET = new Set(TRADES.map(t => tradeBidTemplate(t.key)));

/** True if text is empty or an unedited auto-generated trade template. */
export function isTradeTemplate(text) {
  if (!text || !text.trim()) return true;
  return _TRADE_TEMPLATE_SET.has(text.trim());
}
